import { UK_COUNTIES } from '@pgs/shared';
import { useEffect, useRef } from 'react';
import { type Control, type FieldPath, type FieldValues, useWatch } from 'react-hook-form';
import { AddressAutocomplete, type ParsedAddress } from '@/components/form/AddressAutocomplete';
import { SelectField } from '@/components/form/SelectField';
import { TextField } from '@/components/form/TextField';

interface Props<T extends FieldValues> {
  control: Control<T>;
  register: import('react-hook-form').UseFormRegister<T>;
  errors: import('react-hook-form').FieldErrors<T>;
  prefix: FieldPath<T>;
  setValue: import('react-hook-form').UseFormSetValue<T>;
  labelPostcode?: string;
  labelAddress?: string;
}

const UK_COUNTY_SET = new Set<string>(UK_COUNTIES);

// Google-Places-backed address search + manual fields. The user picks an
// address from the Google suggestions; on select, every field below is
// populated. Users who prefer to type are free to edit or skip the search.
export const AddressSection = <T extends FieldValues>({
  control,
  register,
  errors,
  prefix,
  setValue,
  labelAddress = 'Address',
}: Props<T>) => {
  const err = (field: string) => {
    const node = field.split('.').reduce<unknown>((acc, k) => {
      if (acc && typeof acc === 'object' && k in (acc as object)) {
        return (acc as Record<string, unknown>)[k];
      }
      return undefined;
    }, errors);
    return (node as { message?: string } | undefined)?.message;
  };

  const p = (suffix: string) => `${prefix}.${suffix}` as FieldPath<T>;

  // When the user clears the Postcode, clear the dependent address fields too
  // so the form doesn't keep showing a stale half-address. Tracks the previous
  // postcode so we only fire on the non-empty → empty transition.
  const postcode = useWatch({ control, name: p('postcode') }) as string | undefined;
  const prevPostcode = useRef<string | undefined>(postcode);
  useEffect(() => {
    const wasFilled = (prevPostcode.current ?? '').trim() !== '';
    const isEmpty = (postcode ?? '').trim() === '';
    if (wasFilled && isEmpty) {
      (['houseNumber', 'line1', 'line2', 'town', 'county'] as const).forEach((f) => {
        setValue(p(f), '' as never, { shouldValidate: false, shouldDirty: true });
      });
    }
    prevPostcode.current = postcode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postcode]);

  const applyAddress = (addr: ParsedAddress) => {
    setValue(p('houseNumber'), addr.houseNumber as never, { shouldValidate: true });
    setValue(p('line1'), addr.line1 as never, { shouldValidate: true });
    setValue(p('line2'), addr.line2 as never, { shouldValidate: true });
    setValue(p('town'), addr.town as never, { shouldValidate: true });
    // Only populate the county if Google returns a value that matches our list.
    // Otherwise leave it blank so the user picks from the dropdown.
    const county = UK_COUNTY_SET.has(addr.county) ? addr.county : '';
    setValue(p('county'), county as never, { shouldValidate: true });
    setValue(p('postcode'), addr.postcode as never, { shouldValidate: true });
  };

  return (
    <div>
      <AddressAutocomplete onSelect={applyAddress} />

      <label className="lbl mt-4">{labelAddress}</label>
      <input
        {...register(p('houseNumber'))}
        className={`inp mb-2 ${err('houseNumber') ? 'inp-error' : ''}`}
        placeholder="House number or name"
      />
      {err('houseNumber') && <p className="err-msg">{err('houseNumber')}</p>}
      <input
        {...register(p('line1'))}
        className={`inp mb-2 ${err('line1') ? 'inp-error' : ''}`}
        placeholder="Line 1 (street)"
      />
      {err('line1') && <p className="err-msg">{err('line1')}</p>}
      <input
        {...register(p('line2'))}
        className="inp"
        placeholder="Line 2"
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label="Town" {...register(p('town'))} error={err('town')} />
        <SelectField
          label="County"
          {...register(p('county'))}
          placeholder="Select county..."
          options={UK_COUNTIES.map((c) => ({ value: c, label: c }))}
          error={err('county')}
        />
      </div>
      <TextField label="Postcode" {...register(p('postcode'))} error={err('postcode')} />
    </div>
  );
};
